import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Steps from 'rc-steps';
import Text from '../../typography/Text';
import Icon from '../../general/Icon';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Stepper');
var _ref2 = /*#__PURE__*/React.createElement(Icon, {
  name: "check",
  size: "medium",
  color: "white"
});
function Stepper(_ref) {
  let {
    children,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    onClick,
    steps = [],
    currentStep = 1,
    labelPlacement = 'horizontal',
    withoutLabels = false,
    ...props
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  // Overrides
  const override = getOverrides(overridesProp, Stepper.overrides);

  // Classes
  const rootClassName = useMemo(() => classnames(classes.root, classNameProp, {
    [classes.withoutLabels]: withoutLabels
  }), [classNameProp, classes.root, classes.withoutLabels, withoutLabels]);
  const stepClassNames = useCallback((step, i) => classnames({
    [classes.completed]: step.isCompleted,
    [classes.active]: i === currentStep
  }), [classes.active, classes.completed, currentStep]);
  const isHighlighted = useCallback((step, i) => step.isCompleted || i === currentStep, [currentStep]);
  const getIcon = useCallback(i => {
    let content = i + 1;
    if (steps[i] && steps[i].isCompleted) {
      content = _ref2;
    }
    const highlighted = isHighlighted(steps[i], i);
    return /*#__PURE__*/React.createElement("span", {
      onClick: highlighted ? () => onClick(i) : undefined
    }, /*#__PURE__*/React.createElement(Text, {
      type: "body1",
      className: classes.icon
    }, content));
  }, [classes.icon, isHighlighted, onClick, steps]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: rootClassName
  }, override.root), /*#__PURE__*/React.createElement(Steps, _extends({
    current: currentStep,
    labelPlacement: labelPlacement
  }, props), steps.map((step, i) => {
    const highlighted = isHighlighted(step, i);
    const stepProps = {
      className: stepClassNames(step, i),
      key: `step-${i}`,
      icon: getIcon(i),
      onClick: highlighted ? () => onClick(i) : undefined,
      title: withoutLabels ? undefined : /*#__PURE__*/React.createElement(Text, {
        bold: highlighted
      }, step.title)
    };
    return /*#__PURE__*/React.createElement(Steps.Step, _extends({
      key: `step-${i}`
    }, stepProps));
  })));
}
Stepper.overrides = ['root', 'rc-steps'];
process.env.NODE_ENV !== "production" ? Stepper.propTypes = {
  /** Custom CSS class for styling the root container */
  className: PropTypes.string,
  /** Object with custom style overrides */
  overrides: PropTypes.object,
  /** Array of step objects defining the stepper navigation */
  steps: PropTypes.arrayOf(PropTypes.shape({
    /** Text label displayed for the step */
    title: PropTypes.string,
    /** When true, the step is marked as completed with a check icon */
    isCompleted: PropTypes.bool
  })),
  /** Zero-based index of the current active step */
  currentStep: PropTypes.number,
  /** Callback function triggered when a step is clicked. Receives the step index as parameter */
  onClick: PropTypes.func,
  /** Positioning of step labels relative to step indicators ('horizontal' or 'vertical') */
  labelPlacement: PropTypes.string,
  /** When true, step labels are not displayed, showing only the step indicators */
  withoutLabels: PropTypes.bool
} : void 0;
export default /*#__PURE__*/memo(Stepper);